import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import OrganizationSelector from './OrganizationSelector';
import AuthLoading from '../shared/loading/AuthLoading';
import { Organization } from '../../models';

interface OrganizationSelectorRouteProps {
  userOrganizations: Organization[];
  onSelectOrganization: (organizationId: string) => Promise<void>;
  onClearOrganization?: () => void;
  showOrgSelector: boolean;
  isAuthenticated: boolean;
  authLoading: boolean;
  loading: boolean;
  isGodMode: boolean;
}

const OrganizationSelectorRoute: React.FC<OrganizationSelectorRouteProps> = ({
  userOrganizations,
  onSelectOrganization,
  onClearOrganization,
  showOrgSelector,
  isAuthenticated,
  authLoading,
  loading,
  isGodMode,
}) => {
  const location = useLocation();
  const state = location.state as
    | { orgDeactivated?: boolean; switchOrganization?: boolean }
    | undefined;

  // When redirected here because org was deactivated, clear current organization
  useEffect(() => {
    if (state?.orgDeactivated && onClearOrganization) {
      onClearOrganization();
    }
  }, [state?.orgDeactivated, onClearOrganization]);

  // Show loading while auth state is being determined
  if (authLoading) {
    return <AuthLoading />;
  }

  // If not authenticated, redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Only active organizations can be selected
  const selectableOrganizations = isGodMode
    ? userOrganizations
    : userOrganizations.filter(org => org.active !== false);

  // If user has no organizations at all, redirect to auth
  if (userOrganizations.length === 0) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to home only when this screen should not be shown: user already picked an org
  // and did not open this route explicitly (e.g. "Cambiar organización" passes switchOrganization).
  if (
    !showOrgSelector &&
    !state?.orgDeactivated &&
    !state?.switchOrganization
  ) {
    return <Navigate to="/home" replace />;
  }

  return (
    <OrganizationSelector
      organizations={selectableOrganizations}
      onSelectOrganization={onSelectOrganization}
      loading={loading}
      allOrganizationsDeactivated={!isGodMode && selectableOrganizations.length === 0}
    />
  );
};

export default OrganizationSelectorRoute;
